'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@clerk/nextjs';
import { ApiError } from '@lib/api/api-response';
import { isNonRetryableError } from '@lib/api/utils/is-non-retryable-error';
import { useCurrentUserId } from '@lib/auth/contexts/current-user-context';
import { PORTAL_SHELL_SERVER_SEED_STALE_TIME_MS } from '@lib/portal-shell/constants/server-seed-stale-time';
import { UseQueryResult } from '@lib/query/client';
import { useAuthedQuery } from '@lib/query/use-authed-query';
import { authQueryKeys } from '../auth-query-keys';
import type { AuthMe } from '../types/auth-me';
import { useClerkDegraded } from './use-clerk-degraded';
export type { AuthMe };

// Module-scope selectors keep reference identity stable across renders
// so TanStack's observer skips re-projection on cache hits
const selectUser = (d: AuthMe) => d.user;
const selectSession = (d: AuthMe) => d.session;
export { selectSession, selectUser };

/**
 * Single source for the authenticated user record and the persisted
 * Clerk session row. Wraps `GET /api/auth/me`.
 *
 * Gated by `useAuth().userId` so the query stays disabled until Clerk
 * hydrates, and short-circuits when Clerk is degraded.
 *
 * Sign-in / sign-out auto-invalidates because the queryKey embeds
 * `userId`; callers that mutate the user row should still invalidate
 * `authQueryKeys.me(userId)` explicitly.
 */
export function useAuthMe(select, t0) {
  const $ = _c(6);
  const enabled = t0 === undefined ? true : t0;
  const {
    userId: clerkUserId,
    isLoaded
  } = useAuth();
  const serverUserId = useCurrentUserId();
  const userId = isLoaded ? clerkUserId : clerkUserId ?? serverUserId;
  const isDegraded = useClerkDegraded();
  let t1;
  if ($[0] !== userId) {
    t1 = authQueryKeys.me(userId);
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = enabled && !!userId && !isDegraded;
  let t3;
  if ($[2] !== select || $[3] !== t1 || $[4] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp,
      enabled: t2,
      staleTime: PORTAL_SHELL_SERVER_SEED_STALE_TIME_MS,
      select,
      retry: _temp2
    };
    $[2] = select;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useAuthedQuery(t3);
}
function _temp2(failureCount, error) {
  if (isNonRetryableError(error)) {
    return false;
  }
  return failureCount < 3;
}
async function _temp() {
  const res = await fetch("/api/auth/me");
  if (!res.ok) {
    throw new ApiError(`Failed to fetch /api/auth/me: ${res.status}`, res.status);
  }
  return res.json();
}